import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, Calendar, Clock, User, Loader2 } from "lucide-react";
import { blogAPI } from "../services/api";
import { useToast } from "../hooks/useToast";

const BlogPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const { toast } = useToast();

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadBlogs();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const loadBlogs = async () => {
    if (searchTerm) {
      setSearchLoading(true);
    }

    try {
      const response = await blogAPI.getBlogs({
        search: searchTerm,
        visibility: 'public', // Explicitly filter for public blogs only
        limit: 20
      });

      if (response.success) {
        setBlogs(response.data.blogs || response.data || []);
        setTotalCount(response.data.totalCount || response.data.total || response.data.length || 0);
      } else {
        toast.error(response.error?.message || "Failed to load blogs");
        setBlogs([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Failed to load blogs:", error);
      toast.error("Failed to load blog posts");
      setBlogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-2 text-slate-600">Loading blog posts...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Insights & Analysis
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Stay updated with the latest trends in AI-powered financial intelligence,
            market analysis, and investment strategies.
          </p>
        </div>

        {/* Filters */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full sm:w-80"
                />
                {searchLoading && (
                  <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
                )}
              </div>
              

            </div>

            <div className="text-sm text-slate-600">
              {totalCount} article{totalCount !== 1 ? 's' : ''} found
            </div>
          </div>
        </div>

        {/* Blog Grid */}
        {blogs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogs.map((blog) => (
              <Card key={blog.id} className="border border-slate-200 hover:border-blue-200 transition-all duration-200 hover:shadow-lg group">
                <CardHeader className="space-y-3">
                  <div className="flex items-center justify-between">
                    {blog.tags && blog.tags.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {blog.tags[0]}
                      </Badge>
                    )}
                    <span className="text-xs text-slate-500 flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      {new Date(blog.publishedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  
                  <Link to={`/blog/${blog.slug}`}>
                    <h3 className="text-xl font-semibold text-slate-900 group-hover:text-blue-600 transition-colors duration-200 line-clamp-2">
                      {blog.title}
                    </h3>
                  </Link>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <p className="text-slate-600 line-clamp-3">
                    {blog.excerpt}
                  </p>
                  
                  <div className="flex flex-wrap gap-1">
                    {blog.tags.slice(0, 3).map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                    {blog.tags.length > 3 && (
                      <Badge variant="outline" className="text-xs text-slate-500">
                        +{blog.tags.length - 3} more
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div className="flex items-center text-sm text-slate-500">
                      <User className="w-4 h-4 mr-1" />
                      {blog.author}
                    </div>
                    <div className="flex items-center text-sm text-slate-500">
                      <Clock className="w-4 h-4 mr-1" />
                      {blog.readTime}
                    </div>
                  </div>
                  
                  <Link to={`/blog/${blog.slug}`} className="mt-4 block">
                    <Button variant="default" className="w-full bg-blue-600 hover:bg-blue-700 text-white group-hover:shadow-md transition-all duration-200">
                      Read More
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No articles found</h3>
            <p className="text-slate-600 mb-6">
              Try adjusting your search criteria.
            </p>
            <Button
              onClick={() => {
                setSearchTerm("");
              }}
              variant="outline"
            >
              Clear Search
            </Button>
          </div>
        )}


      </div>
    </div>
  );
};

export default BlogPage;